import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Row,
    Col,
    Typography,
    Spin,
    Alert,
    Breadcrumb,
    Space,
    Button,
    Tag
} from 'antd';
import {
    HomeOutlined,
    TruckOutlined,
    ArrowRightOutlined
} from '@ant-design/icons';
import { Helmet } from 'react-helmet-async';
import MainLayout from '../../layouts/MainLayout';
import { RootState } from '../../redux/store';
import { getTruckTireCategories } from '../../redux/actions/truckTire';

const { Title, Text, Paragraph } = Typography;

const TruckTireCategoriesPage: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { categories, loading, error } = useSelector((state: RootState) => state.truckTires);

    useEffect(() => {
        dispatch(getTruckTireCategories());
    }, [dispatch]);

    const handleCategoryClick = (categoryName: string) => {
        navigate(`/kamyon-lastikleri?grup=${encodeURIComponent(categoryName)}`);
    };

    // Kategori açıklamaları
    const categoryDescriptions = {
        'ASFALT ÇEKER': {
            title: 'Asfalt Çeker Lastikleri',
            description: 'Asfalt zeminde çeker aksı için özel olarak tasarlanmış lastikler. Mükemmel çekiş gücü ve düşük yakıt tüketimi sağlar.',
            color: '#1890ff',
            features: ['Düşük yuvarlanma direnci', 'Üstün çekiş gücü', 'Ekonomik yakıt tüketimi', 'Uzun ömürlü banda']
        },
        'ASFALT DÜZ': {
            title: 'Asfalt Düz Lastikleri',
            description: 'Asfalt yollarda düşük yuvarlanma direnci ile ekonomik sürüş deneyimi sunan lastikler.',
            color: '#52c41a',
            features: ['Düşük yuvarlanma direnci', 'Sessiz çalışma', 'Uzun mesafe performansı', 'Düşük yakıt tüketimi']
        },
        'ASFALT PILOT': {
            title: 'Asfalt Pilot Lastikleri',
            description: 'Asfalt yollarda yön verme aksı için tasarlanmış, üstün yönlendirebilirlik sağlayan lastikler.',
            color: '#722ed1',
            features: ['Hassas direksiyon kontrolü', 'Düşük gürültü seviyesi', 'Uzun kilometraj', 'İyi yol tutuş']
        },
        'HAFRIYAT ÇEKER': {
            title: 'Hafriyat Çeker Lastikleri',
            description: 'Hafriyat ve inşaat sahalarında zorlu arazi koşullarında maksimum çekiş sağlayan lastikler.',
            color: '#fa8c16',
            features: ['Zorlu arazi performansı', 'Maksimum çekiş gücü', 'Dayanıklı banda yapısı', 'Kesik ve delinme direnci']
        },
        'HAFRIYAT DORSE PILOT': {
            title: 'Hafriyat Dorse Pilot Lastikleri',
            description: 'Hafriyat taşımacılığında dorse aksı için özel tasarlanmış dayanıklı lastikler.',
            color: '#eb2f96',
            features: ['Ağır yük kapasitesi', 'Zorlu zemin uyumu', 'Uzun ömürlü yapı', 'Düşük bakım maliyeti']
        },
        'HAFRIYAT ÖN': {
            title: 'Hafriyat Ön Lastikleri',
            description: 'Hafriyat araçlarının ön aksı için tasarlanmış, üstün yönlendirebilirlik ve dayanıklılık sunan lastikler.',
            color: '#f5222d',
            features: ['Güçlü direksiyon kontrolü', 'Zorlu arazi adaptasyonu', 'Yüksek dayanıklılık', 'İyi fren performansı']
        }
    };

    if (loading) {
        return (
            <MainLayout>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Spin size="large" />
                    <p style={{ marginTop: '16px' }}>Kategoriler yükleniyor...</p>
                </div>
            </MainLayout>
        );
    }

    if (error) {
        return (
            <MainLayout>
                <div style={{ padding: '24px' }}>
                    <Alert
                        message="Hata"
                        description={error}
                        type="error"
                        showIcon
                    />
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <Helmet>
                <title>Kamyon Lastiği Kategorileri | Sakarya Lastik</title>
                <meta name="description" content="Kamyon lastik kategorileri: Asfalt çeker, asfalt düz, asfalt pilot, hafriyat çeker, hafriyat dorse pilot, hafriyat ön lastikleri." />
                <meta name="keywords" content="kamyon lastiği kategorileri, asfalt lastik, hafriyat lastik, çeker lastik, pilot lastik" />
            </Helmet>

            <div style={{ padding: '24px' }}>
                {/* Breadcrumb */}
                <Breadcrumb style={{ marginBottom: '16px' }}>
                    <Breadcrumb.Item>
                        <HomeOutlined />
                        <span style={{ marginLeft: '8px' }}>Ana Sayfa</span>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <TruckOutlined />
                        <span style={{ marginLeft: '8px' }}>Kamyon Lastikleri</span>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        Kategoriler
                    </Breadcrumb.Item>
                </Breadcrumb>

                {/* Başlık */}
                <div style={{ textAlign: 'center', marginBottom: '48px' }}>
                    <Title level={1}>
                        <TruckOutlined style={{ marginRight: '12px' }} />
                        Kamyon Lastiği Kategorileri
                    </Title>
                    <Paragraph style={{ fontSize: '16px', maxWidth: '800px', margin: '0 auto' }}>
                        Ağır vasıta araçlarınız için özel olarak tasarlanmış kamyon lastikleri.
                        Her kullanım alanı için optimize edilmiş profesyonel çözümler.
                    </Paragraph>
                </div>

                {/* Kategoriler */}
                <Row gutter={[24, 24]}>
                    {categories.map((category) => {
                        const categoryInfo = categoryDescriptions[category.kategori as keyof typeof categoryDescriptions];

                        if (!categoryInfo) return null;

                        return (
                            <Col xs={24} md={12} lg={8} key={category.kategori}>
                                <Card
                                    hoverable
                                    style={{
                                        height: '100%',
                                        borderLeft: `4px solid ${categoryInfo.color}`,
                                        transition: 'all 0.3s ease'
                                    }}
                                    bodyStyle={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                                    onClick={() => handleCategoryClick(category.kategori)}
                                >
                                    <div style={{ flex: 1 }}>
                                        <div style={{ marginBottom: '16px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <Title level={4} style={{ margin: 0, color: categoryInfo.color }}>
                                                    {categoryInfo.title}
                                                </Title>
                                                <Tag color={categoryInfo.color}>
                                                    {category.urunSayisi} Ürün
                                                </Tag>
                                            </div>
                                        </div>

                                        <Paragraph style={{ marginBottom: '16px' }}>
                                            {categoryInfo.description}
                                        </Paragraph>

                                        <div style={{ marginBottom: '16px' }}>
                                            <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                                                Özellikler:
                                            </Text>
                                            <Space direction="vertical" size="small">
                                                {categoryInfo.features.map((feature, index) => (
                                                    <Text key={index} style={{ display: 'block', fontSize: '13px' }}>
                                                        • {feature}
                                                    </Text>
                                                ))}
                                            </Space>
                                        </div>
                                    </div>

                                    <Button
                                        type="primary"
                                        icon={<ArrowRightOutlined />}
                                        style={{
                                            backgroundColor: categoryInfo.color,
                                            borderColor: categoryInfo.color,
                                            marginTop: 'auto'
                                        }}
                                        block
                                    >
                                        Ürünleri Görüntüle
                                    </Button>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>

                {/* Alt Bilgi */}
                <div style={{
                    textAlign: 'center',
                    marginTop: '48px',
                    padding: '24px',
                    background: '#f5f5f5',
                    borderRadius: '8px'
                }}>
                    <Title level={3}>Tüm Kamyon Lastiklerini Görüntüle</Title>
                    <Paragraph>
                        Kategori seçimi yapmadan tüm kamyon lastiklerimizi incelemek isterseniz
                    </Paragraph>
                    <Button
                        type="primary"
                        size="large"
                        onClick={() => navigate('/kamyon-lastikleri')}
                    >
                        Tüm Ürünleri Görüntüle
                    </Button>
                </div>
            </div>
        </MainLayout>
    );
};

export default TruckTireCategoriesPage;
